// Cell indicators - special status codes displayed in Excel cells
export const CellIndicator = {
  SIGN_IN: '#SIGN_IN',
  LOADING: '#LOADING',
  ERROR: '#ERROR!',
  NO_DATA: '#NODATA',
  NOT_AVAILABLE: '#N/A',
  OFFLINE: '#OFFLINE',
} as const;

// Cell error messages, sized to read in a default-width column: <= 44 chars,
// 50 with the "#ERR: " prefix. The pane has no settings of its own, so anything
// the user has to change points at the web portal page where it is done.
export const CellError = {
  SEAT_LIMIT: 'License exceeded. See bifrostledger.com',
  NO_SUBSCRIPTION: 'No subscription. See bifrostledger.com',
  SUBSCRIPTION_EXPIRED: 'Plan expired. See bifrostledger.com',
  // Distinct from NO_SUBSCRIPTION: they had a trial and it ran out. Telling
  // someone they have no subscription when theirs just lapsed sends them
  // looking for a signup problem that does not exist.
  TRIAL_EXPIRED: 'Trial ended. See bifrostledger.com',
  // The formula targets an ERP the company is not subscribed for — a scoping
  // problem, not a billing one.
  ERP_MISMATCH: 'ERP not included in your plan',
  SUBSCRIPTION_ISSUE: 'Plan issue. See bifrostledger.com',
  // An invoice unpaid 30 days past its due date pauses queries for the whole
  // company until the payment is registered; the portal stays open to pay.
  PAYMENT_OVERDUE: 'Invoice overdue. See bifrostledger.com',
  AUTH_FAILED: 'Sign in required',
  ACCESS_DENIED: 'Access denied. Check your permissions',
  NETWORK_ERROR: 'Network error. Check your connection',
  TIMEOUT: 'Request timed out. Try again',
  RATE_LIMITED: 'Too many requests. Wait a moment',
  SERVER_ERROR: 'Server unavailable. Try again later',
  NO_RESULT: 'No result',
  NO_RESULTS: 'No results returned',
  FORMULA_NAME_REQUIRED: 'Formula name required',
  BATCH_FAILED: 'Batch execution failed',
  // The request was made for another user or company than the one signed in
  // when it finished, so its figure belongs to that session, not this cell.
  SESSION_CHANGED: 'User or company changed. Recalculate',
  // A `cell+cell` argument whose cells could not be read: Excel's own value
  // for it is a sum, which would query the wrong code.
  CELLS_UNREADABLE: 'Could not read cells joined by +. Recalc',
  DEMO_DISABLED: 'Demo companies are off. Switch company',
  NO_ERP_CONNECTION: 'No ERP connected. Portal > ERP',
  // BALANCE will not assume a January year start, so an unconfirmed company
  // gets this instead of a number that looks right and is not.
  FISCAL_YEAR_UNCONFIRMED: 'Fiscal year unconfirmed. Portal > Settings',
  // The ERP refused the stored sign-in; only an admin reconnecting fixes it,
  // so signing in to the add-in again would not help.
  ERP_REAUTH_REQUIRED: 'ERP sign-in expired. Admin: Portal > ERP',
  // The backend's own sentence spells the agreement out and runs past the cell.
  DPA_NOT_ACCEPTED: 'DPA not accepted. Admin: Portal > Settings',
  UNKNOWN: 'An unexpected error occurred',
} as const;

// Format a cell error with the #ERR: prefix, truncating if needed.
// The cell holds a plain string, so Excel imposes no limit this close; the cap
// only stops a runaway server message. Keep it well above the CellError sizes:
// messages that arrive from the backend put the useful part (which formula,
// which parameter) at the end, and a short cap cuts exactly that off.
const MAX_CELL_MESSAGE_LENGTH = 255;

export function formatCellError(message: string): string {
  const prefix = '#ERR: ';
  const maxMsgLength = MAX_CELL_MESSAGE_LENGTH - prefix.length;
  const truncated = message.length > maxMsgLength
    ? `${message.substring(0, maxMsgLength - 3)}...`
    : message;
  return `${prefix}${truncated}`;
}

// Format a cell error for matrix/rows formulas (returns 2D array)
export function formatCellErrorMatrix(message: string): [[string]] {
  return [[formatCellError(message)]];
}

// Map backend error codes to cell-friendly messages
export function getCellErrorForCode(code?: string): string {
  switch (code) {
    case 'SEAT_LIMIT_EXCEEDED':
      return CellError.SEAT_LIMIT;
    case 'NO_SUBSCRIPTION':
      return CellError.NO_SUBSCRIPTION;
    case 'SUBSCRIPTION_INACTIVE':
      return CellError.SUBSCRIPTION_ISSUE;
    case 'SUBSCRIPTION_EXPIRED':
      return CellError.SUBSCRIPTION_EXPIRED;
    case 'TRIAL_EXPIRED':
      return CellError.TRIAL_EXPIRED;
    case 'PAYMENT_OVERDUE_SUSPENDED':
      return CellError.PAYMENT_OVERDUE;
    case 'AUTH_REQUIRED':
      return CellError.AUTH_FAILED;
    case 'ERP_MISMATCH':
      return CellError.ERP_MISMATCH;
    // Ours, not theirs. These mean the check itself failed, so pointing the
    // user at their plan sends them to fix something that is not broken.
    case 'SUBSCRIPTION_CHECK_FAILED':
    case 'ERP_CHECK_FAILED':
      return CellError.SERVER_ERROR;
    case 'DEMO_COMPANIES_DISABLED':
      return CellError.DEMO_DISABLED;
    case 'NO_ERP_CONNECTION':
      return CellError.NO_ERP_CONNECTION;
    case 'FISCAL_YEAR_UNCONFIRMED':
      return CellError.FISCAL_YEAR_UNCONFIRMED;
    case 'ERP_REAUTH_REQUIRED':
      return CellError.ERP_REAUTH_REQUIRED;
    case 'DPA_NOT_ACCEPTED':
      return CellError.DPA_NOT_ACCEPTED;
    default:
      return '';
  }
}

// A per-formula error from a batch: its code's message when the code has one,
// otherwise the server's own text.
export function getCellErrorForResult(result: { error: string; code?: string }): string {
  return getCellErrorForCode(result.code) || result.error;
}

// Map an API error (message + optional code and HTTP status) to the best cell error message
export function getCellErrorForApiError(error: { message: string; code?: string; status?: number }): string {
  const codeMessage = getCellErrorForCode(error.code);
  if (codeMessage) return codeMessage;

  const msg = error.message.toLowerCase();

  if (msg.includes('seat limit') || msg.includes('exceeded its license limit')) {
    return CellError.SEAT_LIMIT;
  }
  if (msg.includes('no active subscription') || msg.includes('please subscribe')) {
    return CellError.NO_SUBSCRIPTION;
  }
  if (msg.includes('subscription') && msg.includes('expired')) {
    return CellError.SUBSCRIPTION_EXPIRED;
  }
  if (msg.includes('subscription')) {
    return CellError.SUBSCRIPTION_ISSUE;
  }
  if (msg.includes('unauthorized') || msg.includes('sign in') || msg.includes('invalid credentials')) {
    return CellError.AUTH_FAILED;
  }
  if (msg.includes('forbidden') || msg.includes('permission')) {
    return CellError.ACCESS_DENIED;
  }
  if (msg.includes('fetch') || msg.includes('network') || msg.includes('connection') || msg.includes('offline')) {
    return CellError.NETWORK_ERROR;
  }
  if (msg.includes('timeout') || msg.includes('aborted')) {
    return CellError.TIMEOUT;
  }
  if (msg.includes('rate limit')) {
    return CellError.RATE_LIMITED;
  }
  // A server error is the response's status, never a number in the text: an ERP refusal naming
  // account 601500 is not one. "Server error" is what ApiService says for a 5xx without a body.
  if ((error.status !== undefined && error.status >= 500 && !error.code) || msg.includes('server error')) {
    return CellError.SERVER_ERROR;
  }

  return error.message || CellError.UNKNOWN;
}

export function isAuthError(message: string): boolean {
  const msg = message.toLowerCase();
  return msg.includes('token') || msg.includes('unauthorized') || msg.includes('sign in');
}
